import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { publicAPI } from '../../lib/api';
import { Calendar, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

export default function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadPosts = useCallback((p) => {
    setLoading(true);
    publicAPI.getBlog(p).then(r => {
      if (p === 1) {
        setPosts(r.data.posts);
      } else {
        setPosts(prev => [...prev, ...r.data.posts]);
      }
      setTotalPages(r.data.pages);
      setTotal(r.data.total);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadPosts(1);
  }, [loadPosts]);

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    loadPosts(next);
  };

  return (
    <>
      <Helmet>
        <title>Blog | Endustriyel Uretim Rehberi</title>
        <meta name="description" content="Endustriyel uretim, CNC isleme, lazer kesim hakkinda yazilar ve bilgiler." />
      </Helmet>

      <section className="bg-slate-50 py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl font-semibold" data-testid="blog-title">Blog</h1>
          <p className="mt-2 text-muted-foreground">
            Endustriyel uretim dunyasindan yazilar ve bilgiler.
            {total > 0 && <span className="ml-1">({total} yazi)</span>}
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {posts.length === 0 && !loading ? (
            <p className="text-center text-muted-foreground py-12">Henuz blog yazisi yayinlanmamis.</p>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {posts.map(post => (
                  <Link key={post.id} to={`/blog/${post.slug}`}>
                    <Card className="h-full hover:shadow-md transition-shadow" data-testid={`blog-card-${post.slug}`}>
                      {post.cover_image && (
                        <img src={post.cover_image} alt={post.title} className="w-full h-48 object-cover rounded-t-xl" loading="lazy" />
                      )}
                      <CardContent className="p-5">
                        <h2 className="font-semibold text-lg mb-2 line-clamp-2">{post.title}</h2>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          {new Date(post.created_at).toLocaleDateString('tr-TR')}
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>

              {/* Load More Button */}
              {page < totalPages && (
                <div className="mt-10 text-center">
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={handleLoadMore}
                    disabled={loading}
                    className="px-10"
                    data-testid="blog-load-more"
                  >
                    {loading ? (
                      <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Yukleniyor...</>
                    ) : (
                      `Daha Fazla Goster (${posts.length} / ${total})`
                    )}
                  </Button>
                </div>
              )}

              {page >= totalPages && posts.length > 0 && (
                <p className="mt-10 text-center text-sm text-muted-foreground">
                  Tum yazilar gosteriliyor ({total} yazi)
                </p>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
}
